import { addHours, startOfDay, endOfDay, format, differenceInMinutes, isWithinInterval, addDays } from 'date-fns';
import type { Event } from '@/hooks/useEvents';

export const HOUR_HEIGHT = 60; // pixels per hour
export const DAY_START_HOUR = 7; // 7 AM
export const DAY_END_HOUR = 22; // 10 PM
export const VISIBLE_HOURS = DAY_END_HOUR - DAY_START_HOUR;

// Generate hourly time slots for a day
export function generateTimeSlots(startHour = DAY_START_HOUR, endHour = DAY_END_HOUR): Date[] {
  const slots: Date[] = [];
  const baseDate = new Date();
  baseDate.setHours(startHour, 0, 0, 0);
  
  for (let i = 0; i <= endHour - startHour; i++) {
    slots.push(addHours(baseDate, i));
  }
  
  return slots;
}

// Calculate event position in pixels
export function calculateEventPosition(
  startTime: Date,
  endTime: Date,
  dayStart: Date,
  hourHeight: number = HOUR_HEIGHT
): { top: number; height: number } {
  const dayStartTime = new Date(dayStart);
  dayStartTime.setHours(DAY_START_HOUR, 0, 0, 0);
  
  const startMinutes = differenceInMinutes(startTime, dayStartTime);
  const duration = differenceInMinutes(endTime, startTime);
  
  const top = (startMinutes / 60) * hourHeight;
  const height = Math.max((duration / 60) * hourHeight, hourHeight / 2); // Minimum 30min height
  
  return { top, height };
}

// Detect overlapping events and assign columns
export function detectOverlaps(events: Event[]): Array<Event & { column: number; totalColumns: number }> {
  if (events.length === 0) return [];
  
  // Sort events by start time
  const sorted = [...events].sort((a, b) => 
    new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
  );
  
  const columns: Array<Event & { column: number; totalColumns: number }>[] = [];
  
  sorted.forEach(event => {
    const eventStart = new Date(event.start_time);
    const eventEnd = new Date(event.end_time);
    
    // Find which existing columns this event overlaps with
    let placedInColumn = false;
    
    for (let col = 0; col < columns.length; col++) {
      const lastEventInColumn = columns[col][columns[col].length - 1];
      const lastEventEnd = new Date(lastEventInColumn.end_time);
      
      // If this event starts after the last event in this column ends, it can go here
      if (eventStart >= lastEventEnd) {
        columns[col].push({ ...event, column: col, totalColumns: columns.length });
        placedInColumn = true;
        break;
      }
    }
    
    // If we couldn't place it in an existing column, create a new column
    if (!placedInColumn) {
      columns.push([{ ...event, column: columns.length, totalColumns: columns.length + 1 }]);
    }
  });
  
  // Update totalColumns for all events
  const result: Array<Event & { column: number; totalColumns: number }> = [];
  const maxColumns = columns.length;
  
  columns.forEach(column => {
    column.forEach(event => {
      result.push({ ...event, totalColumns: maxColumns });
    });
  });
  
  return result;
}

// Format time range
export function formatTimeRange(start: Date, end: Date): string {
  return `${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`;
}

// Check if event is in time range
export function isEventInRange(event: Event, rangeStart: Date, rangeEnd: Date): boolean {
  const eventStart = new Date(event.start_time);
  const eventEnd = new Date(event.end_time);
  
  return isWithinInterval(eventStart, { start: rangeStart, end: rangeEnd }) ||
         isWithinInterval(eventEnd, { start: rangeStart, end: rangeEnd }) ||
         (eventStart <= rangeStart && eventEnd >= rangeEnd);
}

// Get events for a specific day
export function getEventsForDay(events: Event[], day: Date): Event[] {
  const dayStart = startOfDay(day);
  const dayEnd = endOfDay(day);
  
  return events.filter(event => isEventInRange(event, dayStart, dayEnd));
}

// Get days for week view
export function getWeekDays(date: Date): Date[] {
  const days: Date[] = [];
  const startDate = new Date(date);
  startDate.setDate(date.getDate() - date.getDay()); // Start on Sunday
  
  for (let i = 0; i < 7; i++) {
    days.push(addDays(startDate, i));
  }
  
  return days;
}

// Get days for 3-day view
export function getThreeDays(date: Date): Date[] {
  return [
    addDays(date, -1),
    date,
    addDays(date, 1),
  ];
}

// Format hour label
export function formatHourLabel(hour: number): string {
  if (hour === 0) return '12 AM';
  if (hour === 12) return '12 PM';
  if (hour < 12) return `${hour} AM`;
  return `${hour - 12} PM`;
}
